import { useEffect, useMemo, useState } from 'react'
import styles from './ActivityIndicator.module.css'

const ACTIVE_STATUSES = new Set(['pending', 'running', 'waiting', 'processing', 'polling', 'retrying'])

function formatElapsed(milliseconds) {
  const seconds = Math.max(0, Math.floor(milliseconds / 1000))
  if (seconds < 60) return `${seconds}s`
  const minutes = Math.floor(seconds / 60)
  const remainingSeconds = seconds % 60
  if (minutes < 60) return `${minutes}m ${remainingSeconds}s`
  const hours = Math.floor(minutes / 60)
  return `${hours}h ${minutes % 60}m`
}

export function ActivityIndicator({
  status = 'running',
  label,
  detail,
  startedAt,
  progress,
  actions,
  compact = false,
  surface = false,
  className = '',
}) {
  const active = ACTIVE_STATUSES.has(status)
  const startedAtMs = useMemo(() => {
    if (!startedAt) return null
    const parsed = typeof startedAt === 'number' ? startedAt : Date.parse(startedAt)
    return Number.isFinite(parsed) ? parsed : null
  }, [startedAt])
  const [now, setNow] = useState(() => Date.now())

  useEffect(() => {
    if (!active || startedAtMs === null) return undefined
    setNow(Date.now())
    const timer = window.setInterval(() => setNow(Date.now()), 1000)
    return () => window.clearInterval(timer)
  }, [active, startedAtMs])

  const normalizedProgress = Number.isFinite(progress)
    ? Math.max(0, Math.min(100, Number(progress)))
    : null
  const elapsed = startedAtMs === null ? null : formatElapsed(now - startedAtMs)

  return (
    <div
      className={[styles.root, compact && !surface ? styles.compact : '', surface ? styles.surface : '', className].filter(Boolean).join(' ')}
      data-status={status}
    >
      {surface ? (
        <span className={styles.pixelGrid} aria-hidden="true">
          {Array.from({ length: 9 }, (_, index) => <span key={index} />)}
        </span>
      ) : <span className={styles.marker} aria-hidden="true" />}
      <span className={styles.copy} role="status" aria-live="polite" aria-busy={active || undefined}>
        <span className={styles.label}>{label}</span>
        {detail ? <span className={styles.detail}>{detail}</span> : null}
      </span>
      {elapsed ? <span className={styles.elapsed} aria-hidden="true">{elapsed}</span> : null}
      {actions ? <span className={styles.actions}>{actions}</span> : null}
      {normalizedProgress !== null ? (
        <span
          className={styles.progress}
          role="progressbar"
          aria-label={`${label} progress`}
          aria-valuemin="0"
          aria-valuemax="100"
          aria-valuenow={normalizedProgress}
        >
          <span style={{ width: `${normalizedProgress}%` }} />
        </span>
      ) : null}
    </div>
  )
}

export default ActivityIndicator
